import { assertUnreachable, isNotDefined } from '@sgme/fp';

import { logger } from '@/components/SgwtBootstrap/logger.ts';
import type { BlotterOrderStatus } from '@/store/blotter/api/types/BlotterOrderEntryFromBackend.ts';
import type { OptionSide } from '@/store/option/state.ts';
import type { Side } from '@/store/orders/state.ts';

export function mapStatusToColor(status: BlotterOrderStatus | undefined) {
  switch (status) {
    case 'Submitted':
      return 'warning';
    case 'Rejected':
    case 'Cancelled':
    case 'InError':
      return 'danger';
    case 'Expired':
      return 'secondary';
    case 'Done':
    case 'Partially done':
    case 'Finished':
      return 'success';
    case 'Executing':
    case 'Watched':
    case 'Paused':
      return 'info';
    default:
      return 'secondary';
  }
}

const badgePillClass = (color: string) => `badge badge-sm rounded-pill badge-${color}`;

const textClass = (color: string) => `fs-12 text-${color}`;

export function getStatusClass(status: string | null): string {
  switch (status) {
    case 'BookingToBeChecked':
    case 'ClearingPending':
    case 'Electronic CTP unmapped':
    case 'Fxo MO Pending':
    case 'Fxo Trader Pending':
    case 'Hedge Pending':
    case 'MO Validation Pending':
    case 'Pending Full Termination':
    case 'Pending Liquidity':
    case 'Pending Migration':
    case 'Pending RFQ':
    case 'RFQ Pending':
    case 'Sales Pending':
    case 'Trader Pending':
    case 'Waiting Sales':
    case 'Submitted':
      return badgePillClass('warning');
    case 'Block Processed':
    case 'BookingValidated':
    case 'Cleared':
    case 'FO Validated':
    case 'RFQ Validated':
    case 'Cancelling':
    case 'Updating':
    case 'Liquidity Allocated':
    case 'Netted':
    case 'Conversion Executed':
      return badgePillClass('success');
    case 'Booking Pre Validated':
    case 'FO Pre-Validated':
    case 'Executing':
      return badgePillClass('info');
    case 'Cancelled':
    case 'Rejected':
    case 'InError':
    case 'Destroyed':
    case 'FO Destroyed':
    case 'Sales Cancelled':
    case 'Trader Cancelled':
      return textClass('danger');
    case 'Initial':
    case 'Sales RFQ':
    case 'Trader RFQ':
      return textClass('dark');
    case 'Done':
    case 'Finished':
    case 'Partially done':
      return textClass('success');
    case 'Watched':
    case 'Not started':
    case 'Running':
    case 'Paused':
    case 'New':
      return badgePillClass('outline-info');
    case null:
    case 'Expired':
    case 'Missed Deal':
    case 'Simulation':
    case 'SimulDestroyed':
    case 'RFQ Sales Cancelled':
      return textClass('secondary');
    default:
      logger.logWarning(`Received unknown status in blotter: '{status_s}'`, status);
      return textClass('secondary');
  }
}

export function isOrderEditable(status: BlotterOrderStatus | undefined | null, tradeId: string | undefined): boolean {
  if (isSgmeOrder(tradeId) === false) {
    return false;
  }
  switch (status) {
    case 'Watched':
    case 'Executing':
    case 'Paused':
    case 'Running':
    case 'New':
      return true;
    default:
      return false;
  }
}

export function isSgmeOrder(tradeId: string | undefined): boolean {
  // for a lack of a better check for now
  return tradeId?.toUpperCase().startsWith('SGME') ?? false;
}

export function getOrderWay(currencyPair: string, notionalCurrency: string | null, way: OptionSide): Side {
  const currencies = currencyPair?.split('/');

  return notionalCurrency === currencies[0] ? way : invertSide(way);
}

function invertSide(way: Side) {
  switch (way) {
    case 'Both':
      return 'Both';
    case 'Buy':
      return 'Sell';
    case 'Sell':
      return 'Buy';
    default:
      return assertUnreachable(way, 'Unknown way');
  }
}

export function dayAndTimeToDate(day: string | undefined, time: string | undefined) {
  if (isNotDefined(day) || isNotDefined(time)) {
    return undefined;
  }
  const normalizedTime = time
    .split(':')
    .map((part) => part.padStart(2, '0'))
    .join(':');

  const dateTimeString = `${day}T${normalizedTime}`;
  const localDate = new Date(dateTimeString);

  return localDate.toISOString();
}
